import { useState, useEffect, useCallback } from 'react';
import { adminService } from '@/api/services/admin';
import type { Campaign } from '@/api/services/admin';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// ── Helpers ───────────────────────────────────────────────────────────────────

function stableId(c: Campaign): string {
  return c.slug ?? String(c.id ?? '—');
}

function hasMessageTemplate(c: Campaign): boolean {
  return !!(c.schema && 'message_template' in c.schema);
}

// ── Add Campaign inline form ──────────────────────────────────────────────────

interface AddFormProps {
  onSuccess: (id: number | string | undefined, slug: string | undefined) => void;
  onCancel: () => void;
}

function AddCampaignForm({ onSuccess, onCancel }: AddFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schemaRaw, setSchemaRaw] = useState('');
  const [schemaError, setSchemaError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSchemaError('');
    setSubmitError('');

    let parsedSchema: unknown = undefined;
    if (schemaRaw.trim()) {
      try {
        parsedSchema = JSON.parse(schemaRaw);
      } catch {
        setSchemaError('Invalid JSON — check your syntax.');
        return;
      }
    }

    setSubmitting(true);
    adminService
      .addCampaign({ name: name.trim(), description: description.trim() || undefined, schema: parsedSchema })
      .then((result) => {
        const id = result.id ?? result.campaign_id;
        onSuccess(id, result.slug);
      })
      .catch((e) => setSubmitError(e instanceof Error ? e.message : 'Failed to create campaign.'))
      .finally(() => setSubmitting(false));
  };

  return (
    <Card className="border-primary-300 dark:border-primary-700">
      <CardHeader>
        <CardTitle className="text-base">New campaign</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Warm Intro"
            required
          />

          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — short description of this campaign."
          />

          <div className="w-full">
            <label className="block text-sm font-medium text-text-primary mb-1">
              Schema <span className="text-text-muted font-normal">(JSON)</span>
            </label>
            <textarea
              value={schemaRaw}
              onChange={(e) => {
                setSchemaRaw(e.target.value);
                setSchemaError('');
              }}
              rows={14}
              placeholder={'{\n  "message_template": {\n    ...\n  }\n}'}
              className={`w-full px-3 py-2 rounded-sm border font-mono text-xs bg-background text-text-primary
                placeholder:text-text-muted resize-y
                focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500
                ${schemaError
                  ? 'border-error-400 dark:border-error-600'
                  : 'border-border hover:border-border-strong'
                }`}
            />
            {schemaError && (
              <p className="mt-1 text-xs text-error-600 dark:text-error-400">{schemaError}</p>
            )}
          </div>

          {submitError && (
            <div className="p-3 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
              <p className="text-sm text-error-700 dark:text-error-400">{submitError}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" variant="primary" size="sm" disabled={submitting || !name.trim()}>
              {submitting ? 'Creating…' : 'Create campaign'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Edit Campaign inline form ─────────────────────────────────────────────────

interface EditFormProps {
  campaign: Campaign;
  onSuccess: (updated: Campaign) => void;
  onCancel: () => void;
}

function EditCampaignForm({ campaign, onSuccess, onCancel }: EditFormProps) {
  const [name, setName] = useState(campaign.name);
  const [description, setDescription] = useState(campaign.description ?? '');
  const [schemaRaw, setSchemaRaw] = useState(
    campaign.schema ? JSON.stringify(campaign.schema, null, 2) : '',
  );
  const [schemaError, setSchemaError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSchemaError('');
    setSubmitError('');

    let parsedSchema: unknown = undefined;
    if (schemaRaw.trim()) {
      try {
        parsedSchema = JSON.parse(schemaRaw);
      } catch {
        setSchemaError('Invalid JSON — check your syntax.');
        return;
      }
    }

    setSubmitting(true);
    adminService
      .updateCampaign({
        campaign_id: campaign.id,
        name: name.trim(),
        description: description.trim() || undefined,
        schema: parsedSchema,
      })
      .then(() => {
        onSuccess({
          ...campaign,
          name: name.trim(),
          description: description.trim() || undefined,
          schema: parsedSchema as Campaign['schema'],
        });
      })
      .catch((e) => setSubmitError(e instanceof Error ? e.message : 'Failed to update campaign.'))
      .finally(() => setSubmitting(false));
  };

  return (
    <Card className="border-primary-300 dark:border-primary-700">
      <CardHeader>
        <CardTitle className="text-base">Edit campaign — {stableId(campaign)}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Warm Intro"
            required
          />

          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — short description of this campaign."
          />

          <div className="w-full">
            <label className="block text-sm font-medium text-text-primary mb-1">
              Schema <span className="text-text-muted font-normal">(JSON)</span>
            </label>
            <textarea
              value={schemaRaw}
              onChange={(e) => {
                setSchemaRaw(e.target.value);
                setSchemaError('');
              }}
              rows={14}
              placeholder={'{\n  "message_template": {\n    ...\n  }\n}'}
              className={`w-full px-3 py-2 rounded-sm border font-mono text-xs bg-background text-text-primary
                placeholder:text-text-muted resize-y
                focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500
                ${schemaError
                  ? 'border-error-400 dark:border-error-600'
                  : 'border-border hover:border-border-strong'
                }`}
            />
            {schemaError && (
              <p className="mt-1 text-xs text-error-600 dark:text-error-400">{schemaError}</p>
            )}
          </div>

          {submitError && (
            <div className="p-3 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
              <p className="text-sm text-error-700 dark:text-error-400">{submitError}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" variant="primary" size="sm" disabled={submitting || !name.trim()}>
              {submitting ? 'Saving…' : 'Save changes'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Campaigns page ────────────────────────────────────────────────────────────

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [lastCreated, setLastCreated] = useState<{ id?: number | string; slug?: string } | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [updatedId, setUpdatedId] = useState<number | null>(null);

  const fetchCampaigns = useCallback(() => {
    setLoading(true);
    setError('');
    adminService
      .getCampaigns()
      .then((data) => setCampaigns(data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load campaigns.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleSuccess = (id: number | string | undefined, slug: string | undefined) => {
    setLastCreated({ id, slug });
    setShowForm(false);
    fetchCampaigns();
  };

  const handleUpdateSuccess = (updated: Campaign) => {
    setCampaigns((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setEditingId(null);
    setUpdatedId(updated.id);
  };

  // ── Loading / error ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-text-primary">Campaigns</h1>
        <div className="flex items-center gap-3 p-4 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
          <p className="text-sm text-error-700 dark:text-error-400 flex-1">{error}</p>
          <Button variant="secondary" size="sm" onClick={fetchCampaigns}>Retry</Button>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Campaigns</h1>
          <p className="text-sm text-text-secondary mt-1">
            Manage advisor campaigns. Static message-template campaigns use literal{' '}
            <code className="font-mono text-xs bg-surface px-1 py-0.5 rounded">&#123;placeholder&#125;</code>{' '}
            substitution — no LLM.
          </p>
        </div>
        <div className="flex gap-2">
          {!showForm && (
            <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setLastCreated(null); }}>
              + Add Campaign
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={fetchCampaigns}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Last-created banner */}
      {lastCreated && !showForm && (
        <div className="p-3 rounded-md border border-success-200 dark:border-success-800/30 bg-success-50 dark:bg-success-900/20 flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-success-700 dark:text-success-400">Campaign created.</p>
            {lastCreated.id !== undefined && (
              <p className="text-sm text-success-700 dark:text-success-400">
                <span className="font-medium">campaign.id:</span>{' '}
                <code className="font-mono text-xs bg-success-100 dark:bg-success-900/40 px-1 py-0.5 rounded">
                  {String(lastCreated.id)}
                </code>
                {' '}— send this to the advisor-app team as the stable identifier.
              </p>
            )}
            {lastCreated.slug && (
              <p className="text-sm text-success-700 dark:text-success-400">
                <span className="font-medium">slug:</span>{' '}
                <code className="font-mono text-xs bg-success-100 dark:bg-success-900/40 px-1 py-0.5 rounded">
                  {lastCreated.slug}
                </code>
              </p>
            )}
          </div>
          <button
            onClick={() => setLastCreated(null)}
            className="text-success-500 hover:text-success-700 dark:text-success-400 dark:hover:text-success-200 text-lg leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Inline add form */}
      {showForm && (
        <AddCampaignForm
          onSuccess={handleSuccess}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Campaign list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            All campaigns{' '}
            <span className="text-text-muted font-normal text-sm">({campaigns.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {campaigns.length === 0 ? (
            <p className="px-4 py-6 text-sm text-text-muted text-center italic">
              No campaigns yet — add one above.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">ID</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Name</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Slug / stable ID</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Type</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Description</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) =>
                  editingId === c.id ? (
                    <tr key={c.id} className="border-b border-border last:border-0">
                      <td colSpan={6} className="p-4 bg-surface">
                        <EditCampaignForm
                          campaign={c}
                          onSuccess={handleUpdateSuccess}
                          onCancel={() => setEditingId(null)}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={c.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-mono text-xs text-text-primary tabular-nums">
                        {c.id ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-sm text-text-primary font-medium">{c.name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-text-secondary">
                        {stableId(c)}
                      </td>
                      <td className="px-4 py-2">
                        {hasMessageTemplate(c) ? (
                          <Badge variant="info">message_template</Badge>
                        ) : (
                          <Badge variant="neutral">—</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-text-secondary max-w-xs truncate" title={c.description}>
                        {c.description ?? <span className="italic text-text-muted">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {updatedId === c.id && (
                            <span className="text-xs text-success-600 dark:text-success-400">Saved ✓</span>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setEditingId(c.id);
                              setUpdatedId(null);
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
